import React, { useState } from 'react';
import { View } from 'react-native';
import {
  VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLabel, VictoryVoronoiContainer,
} from 'victory';
import Colors from '../constants/Colors';

interface ChildrenBarGraphWebProps {
  data,
  comparisonData,
  compareToggled: boolean,
}

const ChildrenBarGraphWeb = ({
  data, comparisonData, compareToggled,
}: ChildrenBarGraphWebProps) => {
  const [graphContainerWidth, setGraphContainerWidth] = useState<number>(600);

  const graphHeight: number = 405;
  const comparisonBarWidth: number = 24;

  class CustomFlyout extends React.Component {
    render() {
      const { x, y, datum } = this.props as any;
      // eslint-disable-next-line no-underscore-dangle
      const index: number = datum._x;
      const containerHeight: number = 100;
      const maximumValue: number = Math.max(
        data.map((item: { count: number }) => item.count)
          .reduce((prev, next) => Math.max(prev, next), 0) ?? 0,
        comparisonData?.map((item: { count?: number }) => item?.count ?? 0)
          .reduce((prev, next) => Math.max(prev, next), 0) ?? 0,
      );
      const currentValue: number = Math.max(
        data[index]?.count ?? 0,
        comparisonData[index]?.count ?? 0,
      );
      let containerY: number = currentValue > maximumValue * 0.4
        ? Math.max(y, 50 + ((graphHeight - 50) * ((maximumValue - currentValue) / maximumValue)))
        : Math.min(y, ((graphHeight - 50) * ((maximumValue - currentValue) / maximumValue))
          - containerHeight - 50);
      const asset = data[index];
      const name: string = asset?.assetName;
      const template: string = asset?.templateName;
      const location: string = asset?.location;
      const containerX: number = Math.min(
        x,
        graphContainerWidth - (compareToggled ? 280 : 140),
      ) - 40;
      containerY = Math.min(containerY, graphHeight - containerHeight - 28);
      const headerY: number = 20;
      const firstLabelY: number = headerY + 25;
      const secondLabelY: number = firstLabelY + 20;
      const thirdLabelY: number = secondLabelY + 20;
      const startX: number = 10;
      const maxLength: number = Math.max(
        (name?.length ?? 0) + 1,
        datum.count ? datum.count.toString().length + 9 : 0,
        template ? template.length + 11 : 0,
        location ? location.length + 10 : 0,
      );
      const containerWidth: number = 10 + maxLength * 7;

      const label = (dx: number, dy: number, content: string, fill?: string) => <text
        x={containerX + dx}
        y={containerY + dy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={fill ?? Colors.light.hereworksBlack400}
      >
        {content}
      </text>;

      return <svg>
        <rect
          x={containerX}
          y={containerY}
          width={containerWidth}
          height={containerHeight}
          stroke={Colors.light.hereworksDarkBlue}
          rx={5}
          fill="white"
        />
        {label(startX, headerY, name, Colors.light.hereworksBlack600)}
        {label(startX, firstLabelY, 'Tickets: ')}
        {label(60, firstLabelY, datum.count, Colors.light.hereworksBlack600)}
        {compareToggled
          && label(
            60 + datum.count.toString().length * 10,
            firstLabelY,
            comparisonData[index]?.count ?? '-',
            Colors.light.hereworksYellow600,
          )}
        {label(startX, secondLabelY, 'Template: ')}
        {label(73, secondLabelY, template, Colors.light.hereworksBlack600)}
        {label(startX, thirdLabelY, 'Location: ')}
        {label(67, thirdLabelY, location, Colors.light.hereworksBlack600)}
      </svg>;
    }
  }

  const maxCount: number = Math.max(
    ...(data ?? []).map((item) => item.count),
    ...(comparisonData ?? []).map((item) => item?.count ?? 0),
    1,
  );

  return (
    <View onLayout={({ nativeEvent: { layout: { width } } }) => setGraphContainerWidth(width)}>
      <VictoryChart
        containerComponent={<VictoryVoronoiContainer
          voronoiDimension="x"
          labels={({ datum }) => datum}
          labelComponent={<CustomFlyout />}
        />}
        width={graphContainerWidth}
        height={graphHeight}
        domainPadding={{ y: [0, 0] }}
        domain={{ x: [0, 6], y: [0, 1.2 * maxCount] }}
        padding={{
          left: 75,
          right: 170,
          top: 40,
          bottom: 24,
        }}
        singleQuadrantDomainPadding={{ x: false }}
      >
        <VictoryAxis
          disableInlineStyles
          fixLabelOverlap={true}
          dependentAxis
          tickCount={Math.min(10, maxCount)}
          tickFormat={Math.floor}
          style={{
            axis: { stroke: 'none' },
            tickLabels: {
              stroke: Colors.light.hereworksBlack400,
              fontFamily: 'Poppins_400Regular',
              fontSize: 14,
              strokeWidth: 0,
            },
            grid: {
              stroke: Colors.light.hereworksWhite100,
              strokeWidth: 2,
              pointerEvents: 'none',
            },
          }}
          tickLabelComponent={<VictoryLabel dx={-35} />}
        />
        <VictoryAxis
          disableInlineStyles
          fixLabelOverlap={true}
          axisLabelComponent={
            <VictoryLabel
              style={{
                stroke: Colors.light.hereworksYellow500,
                fontSize: 12,
              }}
            />}
          tickCount={20}
          tickValues={data?.map((_, index: number) => index) ?? []}
          tickFormat={(t: number) => (t + 1 > data?.length ? ''
            : data?.[t]?.assetName?.substring(0, 10) ?? '')}
          style={{
            axis: { stroke: 'none' },
            tickLabels: {
              stroke: Colors.light.hereworksBlack400,
              fontFamily: 'Poppins_400Regular',
              fontSize: 14,
              strokeWidth: 0,
            },
          }}
        />
        {compareToggled ? (
          <VictoryGroup offset={comparisonBarWidth}>
            <VictoryBar
              style={{
                data: {
                  fill: ({ active }) => (active
                    ? Colors.light.hereworksDarkBlue : Colors.light.hereworksDarkBlue100),
                },
                labels: { fontSize: 15, stroke: 'black' },
              }}
              y={'count'}
              data={data ?? []}
              barWidth={comparisonBarWidth}
              cornerRadius={{ top: 2 }}
            />
            <VictoryBar
              style={{
                data: {
                  fill: ({ active }) => (active
                    ? Colors.light.hereworksYellow500 : Colors.light.hereworksYellow100),
                },
                labels: { fontSize: 15, stroke: 'black' },
              }}
              y={'count'}
              data={comparisonData ?? []}
              barWidth={comparisonBarWidth}
              cornerRadius={{ top: 2 }}
            />
          </VictoryGroup>
        ) : <VictoryBar
          style={{
            data: {
              fill: ({ active }) => (active
                ? Colors.light.hereworksDarkBlue : Colors.light.hereworksDarkBlue100),
            },
            labels: { fontSize: 15, stroke: 'black' },
          }}
          y={'count'}
          data={data ?? []}
          barWidth={48}
          cornerRadius={{ top: 2 }}
        />}
      </VictoryChart>
    </View>
  );
};

export default ChildrenBarGraphWeb;
